import { useMemo, useState } from "react";
import copyTextToClipboard from "./copyToCLipBoard";
import { FiCopy } from "react-icons/fi";
import { FaCheck } from "react-icons/fa6";
import { FaPhone, FaPhoneSlash } from "react-icons/fa";
import { formatPhoneNumber } from "./PropertySummary";
import ToggleButton from "../core/ToggleButton";
import ToolbarButton from "../SearchListings/ToolbarButton";
import { ContactInfo } from "src/types/listing";
import PhoneTypeIcon from "./PhoneTypeIcon";

export const CONTACT_CARD_BUTTONS = {
  copy: "copy",
  call: "call",
  disable: "disable",
};

export enum LayoutType {
  vertical = "vertical",
  horizontal = "horizontal",
}

export type ContactCardProps = {
  contact: ContactInfo;
  discoveryTooltip?: string;
  onDiscoveryTooltipNextClick?: () => void;
  isLastDiscoveryTooltip?: boolean;
  layoutType?: LayoutType;
  className?: string;
  onToggleIsDisabled?: (isDisabled: boolean) => void;
  isEditingDisabled?: boolean;
};

const ContactCard = ({
  contact,
  discoveryTooltip,
  onDiscoveryTooltipNextClick,
  isLastDiscoveryTooltip = false,
  layoutType = LayoutType.horizontal,
  className,
  onToggleIsDisabled,
  isEditingDisabled = false,
}: ContactCardProps) => {
  const {
    fullName,
    phone,
    dnc,
    hidden: isDisabled,
    isFavorite,
    phoneType,
  } = contact;
  const [isCopied, setIsCopied] = useState(false);
  const contentOpacityClassName = isDisabled ? "opacity-50" : "";

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(phone);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${phone}`;
  };

  const borderClassName = useMemo(() => {
    if (isFavorite) {
      return "border-success border-2";
    }

    if (isDisabled) {
      return "border-[rgb(255,99,71)/0.5]";
    }

    return "border-gray-secondary";
  }, [isFavorite, isDisabled]);

  return (
    <div
      className={`contact-card-root flex w-[300px] rounded-lg p-[8px] border gap-[8px] text-gray-primary ${borderClassName} ${
        layoutType === LayoutType.horizontal
          ? "flex-row justify-between"
          : "flex-col"
      } ${className}`}
    >
      <div className={`flex flex-col gap-[4px] text-[12px] ${contentOpacityClassName}`}>
        <div className="flex flex-row justify-between font-bold leading-4">
          {fullName && fullName !== " " ? (
            <p>{fullName.toUpperCase()}</p>
          ) : (
            <p className="italic">No name found</p>
          )}
        </div>
        <div className="flex gap-1 items-end leading-3">
          <PhoneTypeIcon type={phoneType} dnc={dnc} />
          <p
            className={
              `relative text-nowrap ` +
              (dnc
                ? "text-red-500 fill-red-500"
                : "text-success fill-success font-[700]")
            }
          >
            {formatPhoneNumber(phone)}
          </p>
          {dnc && (
            <p className="text-red-500 font-bold">DNC</p>
          )}
        </div>
      </div>
      <div className="contact-card-buttons flex flex-row items-center justify-center gap-2">
        {!isEditingDisabled && (
          <ToggleButton
            {...(discoveryTooltip === CONTACT_CARD_BUTTONS.disable && {
              DiscoveryTooltipProps: {
                content:
                  "Click to make contact less visible when contact is not interested",
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onNext: onDiscoveryTooltipNextClick || (() => {}),
                isLast: isLastDiscoveryTooltip,
              },
            })}
            isEnabled={!isDisabled}
            onChange={() => onToggleIsDisabled?.(!isDisabled)}
            disabledClassName="bg-red-600"
            enabledClassName="bg-green-500"
          />
        )}
        <ToolbarButton
          {...(discoveryTooltip === CONTACT_CARD_BUTTONS.copy && {
            DiscoveryTooltipProps: {
              content: "Copy the phone number to your clipboard",
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onNext: onDiscoveryTooltipNextClick || (() => {}),
              isLast: isLastDiscoveryTooltip,
            },
          })}
          disabled={isDisabled}
          icon={isCopied ? <FaCheck className="text-success" /> : <FiCopy />}
          onClick={handleCopy}
        />
        <ToolbarButton
          {...(discoveryTooltip === CONTACT_CARD_BUTTONS.call && {
            DiscoveryTooltipProps: {
              content: "Click to start a call with this contact",
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onNext: onDiscoveryTooltipNextClick || (() => {}),
              isLast: isLastDiscoveryTooltip,
            },
          })}
          icon={!isDisabled ? <FaPhone /> : <FaPhoneSlash />}
          disabled={isDisabled}
          onClick={handlePhoneClick}
        />
      </div>
    </div>
  );
};

export default ContactCard;
